import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent } from 'angular-oauth2-oidc';
import { jwtDecode, JwtPayload as JwtPayloadBase } from 'jwt-decode';
import {
  BehaviorSubject,
  combineLatest,
  from,
  Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { orgAdminSelectedOrgIdLocalStorageKey } from 'src/app/core/localstorage';
import { ORG_ADMIN } from 'src/app/core/models/constants';
import {
  ConfigService,
  ServiceWithConfigBase,
} from './config.service';
import { EnumUtils } from './helpers/enum-utils';
import { KeycloakAdapter } from './keycloak-adapter';
import {
  FeatureGate,
  FeatureGateEnum,
  PermissionScope,
} from './models/api.model';
import { UIApplication } from './models/ui.model';
import { CustomOAuthService } from './oauth.service';

interface JwtPayload extends JwtPayloadBase {
  groups?: string[];
  authorization: {
    permissions: Permission[];
  };
  realm_access: { roles: string[] };
}

interface Permission {
  rsname: string;
  scopes: string[];
}

interface KeycloakAuthorizationInstance {
  rpt: string | null;
  entitlement: (resourceServerId: string) => Promise<string>;
}

interface UserInfo {
  application_whitelist: UIApplication[];
  email: string;
}

declare let KeycloakAuthorization: new (
  adapter: KeycloakAdapter,
) => KeycloakAuthorizationInstance;

@Injectable()
export class AuthService extends ServiceWithConfigBase {
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(
    false,
  );
  public isAuthenticated$ =
    this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private hasIdentityClaimsSubject$ = new BehaviorSubject<boolean>(
    false,
  );
  public hasIdentityClaims$ =
    this.hasIdentityClaimsSubject$.asObservable();

  private isDoneInitializing$: Observable<void>;

  public isDoneLoadingPermissions$: ReplaySubject<boolean> =
    new ReplaySubject<boolean>();
  public isDoneLoadingPermissionsObs$ =
    this.isDoneLoadingPermissions$.asObservable();

  private keycloakAdapter: KeycloakAdapter;
  private keycloakAuthorization: KeycloakAuthorizationInstance;

  private _permissions: Map<string, PermissionScope[]> = new Map<
    string,
    PermissionScope[]
  >();
  private _featureGates: FeatureGateEnum[] = [];
  private _roles: string[] = [];

  private _shouldUsePasswordAuth: boolean = false;
  public get shouldUsePasswordAuth(): boolean {
    return this._shouldUsePasswordAuth;
  }

  private _userEmail: string = null;

  public get userEmail(): string {
    return this._userEmail;
  }

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> =
    combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
      map((values) => values.every((b) => b)),
    );

  public navigateToLoginPage(targetUrl?: string) {
    // const extras: NavigationExtras = {
    //   queryParams: {
    //     returnUrl: targetUrl || this.router.url,
    //   },
    // };
    //

    this.login(targetUrl);
  }

  constructor(
    private oauthService: CustomOAuthService,
    private router: Router,
    configService: ConfigService,
  ) {
    super(configService);

    this.isDoneInitializing$ = this.wrapForConfig((config) => {
      if (config.authConfig) {
        this.oauthService.configure(config.authConfig);
      } else {
        throw new Error('No valid auth config provided');
      }

      this.keycloakAdapter = new KeycloakAdapter(this, config);
      this.keycloakAuthorization = new KeycloakAuthorization(
        this.keycloakAdapter,
      );

      this._shouldUsePasswordAuth = config.usePasswordAuth;

      // Useful for debugging:
      this.oauthService.events.subscribe((event) => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuthErrorEvent Object:', event);
        }
      });

      // This is tricky, as it might cause race conditions (where access_token is set in another
      // tab before everything is said and done there.
      // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
      window.addEventListener('storage', (event) => {
        // The `key` is `null` if the event was caused by `.clear()`
        if (event.key !== 'access_token' && event.key !== null) {
          return;
        }

        console.warn(
          'Noticed changes to access_token (most likely from another tab), updating isAuthenticated',
        );
        this.isAuthenticatedSubject$.next(
          this.oauthService.hasValidAccessToken(),
        );

        if (!this.oauthService.hasValidAccessToken()) {
          this.navigateToLoginPage();
        }
      });

      this.oauthService.events.subscribe((e) => {
        if (e.type === 'token_received') {
          this.oauthService.loadUserProfile();
        }

        if (
          this.oauthService.hasValidAccessToken() &&
          (this._permissions.size === 0 ||
            e.type === 'token_received')
        ) {
          this.loadPermissions();
        }

        if (
          [
            'token_error',
            'token_refresh_error',
            'session_terminated',
            'session_error',
          ].includes(e.type)
        ) {
          sessionStorage.clear();
          this.oauthService.stopAutomaticRefresh();
        }
        this.isAuthenticatedSubject$.next(
          this.oauthService.hasValidAccessToken(),
        );
        this.hasIdentityClaimsSubject$.next(
          this.oauthService.getIdentityClaims() !== null,
        );
      });

      this.hasIdentityClaimsSubject$.subscribe(
        (hasIdentityClaims) => {
          if (hasIdentityClaims) {
            const identityClaims = this.identityClaims;
            if (identityClaims['feature_gates']) {
              this._featureGates = [];
              identityClaims['feature_gates'].map(
                (fg: FeatureGate) => {
                  this._featureGates.push(
                    EnumUtils.getValueFromEnum(
                      fg.id,
                      FeatureGateEnum,
                    ),
                  );
                },
              );
            }
          }
        },
      );

      this.oauthService.setupAutomaticSilentRefresh();

      return of(undefined);
    }).pipe(shareReplay(1));
  }

  public runInitialLoginSequence() {
    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:

    return this.isDoneInitializing$.pipe(
      mergeMap(() => from(this.oauthService.loadDiscoveryDocument())),
      // 1. HASH LOGIN:
      // Try to log in via hash fragment after redirect back
      // from IdServer from initImplicitFlow:
      mergeMap(() => from(this.oauthService.tryLogin())),
      mergeMap(() => {
        if (
          this.oauthService.hasValidAccessToken() ||
          !this.oauthService.getRefreshToken()
        ) {
          return of(true);
        }
        // 2. SILENT LOGIN:
        // Try to log in via a refresh because then we can prevent
        // needing to redirect the user:
        return from(this.oauthService.refreshToken()).pipe(
          map(() => true),
          catchError((error) => {
            // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
            // Only the ones where it's reasonably sure that sending the
            // user to the IdServer will help.
            const errorResponsesRequiringUserInteraction = [
              'interaction_required',
              'login_required',
              'account_selection_required',
              'consent_required',
            ];

            if (
              error &&
              error.reason &&
              errorResponsesRequiringUserInteraction.indexOf(
                error.reason.error,
              ) >= 0
            ) {
              // 3. ASK FOR LOGIN:
              // At this point we know for sure that we have to ask the
              // user to log in, so we redirect them to the IdServer to
              // enter credentials.
              //
              // Enable this to ALWAYS force a user to login.
              // this.login();
              //
              // Instead, we'll now do this:
              console.warn(
                'User interaction is needed to log in, we will wait for the user to manually log in.',
              );
              return of(true);
            }

            // We can't handle the truth, just pass on the problem to the
            // next handler.
            throw error;
          }),
        );
      }),
      tap({
        next: () => {
          this.isDoneLoadingSubject$.next(true);

          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (
            this.oauthService.state &&
            this.oauthService.state !== 'undefined' &&
            this.oauthService.state !== 'null'
          ) {
            let stateUrl = this.oauthService.state;
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            this.router.navigateByUrl(stateUrl);
          }
        },
        error: (err) => {
          this.isDoneLoadingSubject$.next(true);
          console.error(err);
        },
      }),
    );
  }

  public login(targetUrl?: string) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    localStorage.removeItem(orgAdminSelectedOrgIdLocalStorageKey);
    sessionStorage.clear();
    this.oauthService.stopAutomaticRefresh();
    this.oauthService.logOut();
  }

  public loadPermissions() {
    try {
      this.keycloakAuthorization.rpt = null;
      this.keycloakAuthorization
        .entitlement(this.keycloakAdapter.resourceServerId)
        .then((rpt: string) => {
          const decodedToken = jwtDecode<JwtPayload>(rpt);
          const permissions = decodedToken.authorization.permissions;
          const newPermissions = new Map<string, PermissionScope[]>();
          permissions.forEach((p) => {
            if (!p.scopes || p.scopes.length === 0) {
              return;
            }
            const scopes: PermissionScope[] = [];
            p.scopes.forEach((s: string) =>
              scopes.push(
                EnumUtils.convertStringToEnum(PermissionScope, s),
              ),
            );
            newPermissions.set(p.rsname, scopes);
          });
          this._permissions = newPermissions;
          this._roles = decodedToken.realm_access.roles;
          this.isDoneLoadingPermissions$.next(true);
        });
    } catch (error) {
      console.error(error);
      localStorage.clear();
      window.location.reload();
    }
  }

  public refresh() {
    return this.oauthService.refreshToken();
  }
  public silentRefresh() {
    return this.oauthService.silentRefresh();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get permissions() {
    return this._permissions;
  }
  public get featureGates() {
    return this._featureGates;
  }

  public get roles() {
    return this._roles;
  }

  public get organisationId() {
    if (this.roles.includes(ORG_ADMIN)) {
      const selectedOrganisationId = localStorage.getItem(
        orgAdminSelectedOrgIdLocalStorageKey,
      );

      if (selectedOrganisationId) {
        return selectedOrganisationId;
      }
    }

    const decodedToken = jwtDecode<JwtPayload>(this.accessToken);

    if (decodedToken.groups) {
      const groups = decodedToken.groups;
      if (groups.length > 0) {
        return groups[0];
      }
    }
    return null;
  }

  public setSelectedOrganisationId(organisationId: string): void {
    if (organisationId) {
      localStorage.setItem(
        orgAdminSelectedOrgIdLocalStorageKey,
        organisationId,
      );
    } else {
      localStorage.removeItem(orgAdminSelectedOrgIdLocalStorageKey);
    }
  }

  public getUserInfo(): Observable<UserInfo> {
    return this.wrapForConfig((config) => {
      return from(
        fetch(
          `${config.authConfig.issuer}/protocol/openid-connect/userinfo`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Bearer ${this.accessToken}`,
            },
          },
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `HTTP error! status: ${response.status}`,
              );
            }
            return response.json();
          })
          .then((userInfo: UserInfo) => {
            this._userEmail = userInfo.email;
            return userInfo;
          }),
      );
    });
  }
}
